import "./Settings.scss";

import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectUser } from "../../core/auth/authSlice";
import BillingApiRoutes from "../../shared/api-routes/billing/billing.api-routes";
import { User } from "../../shared/models";
import http from "../../shared/utils/http";

type SettingsSection = "menu" | "subscription" | "account";

function Settings() {
	const navigate = useNavigate();
	const user = useSelector(selectUser) as User;
	const [activeSection, setActiveSection] = useState<SettingsSection>("menu");
	const [loading, setLoading] = useState(false);

	const handleSubscriptionClick = async () => {
		try {
			setLoading(true);
			const response = await http.post<{ url: string }>(
				BillingApiRoutes.PORTAL
			);
			if (response.url) {
				window.location.href = response.url;
			}
		} catch (err) {
			console.error("Error accessing billing portal:", err);
			alert("Failed to access billing portal. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const allMenuItems = [
		{
			id: "subscription" as SettingsSection,
			title: "Subscription Management",
			description: "Manage your subscription plan and billing",
			icon: "💳",
			onClick: handleSubscriptionClick,
			showIf: user?.accountLevel && user.accountLevel !== "free",
		},
		{
			id: "account" as SettingsSection,
			title: "Account Settings",
			description: "Manage your account",
			icon: "⚙️",
			onClick: () => setActiveSection("account"),
			showIf: true,
		},
	];

	const menuItems = allMenuItems.filter((item) => item.showIf);

	const renderMenu = () => (
		<div className="settings__menu">
			<h1 className="settings__title">Settings</h1>
			<p className="settings__subtitle">Manage your account preferences</p>

			<div className="settings__menu-items">
				{menuItems.map((item) => (
					<button
						key={item.id}
						className="settings__menu-item"
						onClick={item.onClick}
						disabled={loading}
					>
						<div className="settings__menu-item-icon">{item.icon}</div>
						<div className="settings__menu-item-content">
							<h3 className="settings__menu-item-title">
								{loading && item.id === "subscription" ? "Loading..." : item.title}
							</h3>
							<p className="settings__menu-item-description">
								{item.description}
							</p>
						</div>
						<span className="settings__menu-item-arrow">›</span>
					</button>
				))}
			</div>
		</div>
	);

	const renderContent = () => {
		switch (activeSection) {
			case "menu":
				return renderMenu();
			case "account":
				// Import dynamically
				const AccountSettings = require("./AccountSettings").default;
				return (
					<div className="settings__content">
						<button
							className="settings__back-button"
							onClick={() => setActiveSection("menu")}
						>
							← Back to Settings
						</button>
						<AccountSettings />
					</div>
				);
			default:
				return renderMenu();
		}
	};

	return <div className="settings">{renderContent()}</div>;
}

export default Settings;
